import { ActionIcon, Tooltip } from "@mantine/core";
import { lazy, Suspense, useCallback, useRef, useState } from "react";
import { useSnapshot } from "valtio";
import {
	assistantStore,
	closeAssistant,
	openAssistant,
} from "@/store/assistant";
import {
	fillTemplate,
	isWallVoiceEnabled,
	wallNocFor,
} from "./assistant.logic";
import { AssistantFabOrb } from "./assistant-fab-orb";
import { AssistantGuideCard } from "./assistant-guide-card";
import { AssistantReturnButton } from "./assistant-return-button";
import { FAB_FOOTPRINT_PX, ORB_CANVAS_FAB_PX } from "./orb/orb.config";
import { AssistantCursor, cancelPointer, usePointerCancel } from "./pointer";
import { useAssistantAccess, useAssistantText } from "./use-assistant-access";

const AssistantPanel = lazy(() =>
	import("./assistant-panel").then((m) => ({ default: m.AssistantPanel })),
);

export type AssistantVariant = "default" | "wall";

/** z-index di atas konten & header AppShell, di bawah modal Mantine (200). */
const FAB_Z_INDEX = 190;

/**
 * Tombol AI melayang kanan bawah + panel (dimuat lazy saat pertama dibuka).
 * Tidak dirender bila user tidak memenuhi syarat (lihat shouldShowFab).
 */
export function AssistantFab({
	variant = "default",
}: {
	variant?: AssistantVariant;
}) {
	const access = useAssistantAccess();
	const text = useAssistantText();
	const { open } = useSnapshot(assistantStore);
	const fabRef = useRef<HTMLButtonElement>(null);
	const [panelLoaded, setPanelLoaded] = useState(false);

	usePointerCancel();

	const handleClose = useCallback(() => {
		cancelPointer();
		closeAssistant();
		// Panel tidak mengunci fokus, jadi fokus dikembalikan manual ke FAB.
		requestAnimationFrame(() => fabRef.current?.focus());
	}, []);

	if (!access.visible || !access.status) return null;
	const label = fillTemplate(text.fabLabel, {
		name: access.status.assistantName,
	});
	const orbCanvas = ORB_CANVAS_FAB_PX[access.status.orb.size];

	return (
		<>
			<AssistantCursor />
			<AssistantGuideCard />
			<AssistantReturnButton />
			<Tooltip label={label} position="left" withArrow>
				<ActionIcon
					ref={fabRef}
					aria-label={label}
					aria-expanded={open}
					size={FAB_FOOTPRINT_PX}
					radius="xl"
					// Latar transparan (hanya hover yang memberi isian samar) supaya tidak
					// ada lingkaran solid di belakang orb. Footprint tetap; orb boleh
					// meluber dari sini (overflow visible), jadi tata letak tak bergeser.
					variant="subtle"
					color="gray"
					onClick={() => {
						setPanelLoaded(true);
						openAssistant();
					}}
					style={{
						position: "fixed",
						// Di /wall, tombol "Layar penuh" menempati pojok kanan bawah.
						bottom: variant === "wall" ? 56 : 24,
						right: 24,
						zIndex: FAB_Z_INDEX,
						overflow: "visible",
					}}
				>
					<AssistantFabOrb
						config={access.status.orb}
						name={access.status.assistantName}
						size={orbCanvas}
					/>
				</ActionIcon>
			</Tooltip>
			{panelLoaded || open ? (
				<Suspense fallback={null}>
					<AssistantPanel
						status={access.status}
						allowed={access.allowed}
						pathname={access.pathname}
						withHeader={variant !== "wall"}
						voice={isWallVoiceEnabled(variant, access.status.wallVoiceMode)}
						wallNoc={wallNocFor(variant, access.status)}
						onClose={handleClose}
					/>
				</Suspense>
			) : null}
		</>
	);
}
